import { format } from 'date-fns';
import moment from 'moment';
import React from 'react';

import {
  ActiveModifiers,
  DateRange,
  DayPickerProps,
  // eslint-disable-next-line no-restricted-syntax
  DayPicker as ReactDayPicker,
  isDayPickerMultiple,
  isDayPickerRange,
  isDayPickerSingle,
} from 'react-day-picker';
import { LanguageDirection } from 'utils/enums/language';
import { getCurrentDirection } from 'utils/helpers';
import useLocaleState from 'utils/hooks/use-locale-state';

/** Strips away timezone information from the Date/DateRange objects. Useful when dates were created using
 * a time-zoned moment.js object and are needed to be displayed as date range in components like DayPicker,
 * (react-day-picker) which do not support timezone information.
 * For example, let's assume we have dates range in UTC that we need to display in DayPicker, and that the
 * local time is CST timezone (GMT-0600). This is how an arbitrary input date would be processed:
 * UTC Date Input:  Mon Dec 05 2022 11:26:10 GMT-0000 (UTC, app-settings timezone)
 * CST Date Output: Mon Dec 05 2022 11:26:10 GMT-0600 (CST, local-machine timezone)
 * The returned object is meant to represent the same dates range, stripping away any non-local timezone.
 * Now DayPicker will display the correct date ("05/12/2022" in the example above), regardless of timezones.
 */
const localDate = (date?: Date): Date | undefined => date && moment(date).local(true /** keepLocalTime */).toDate();

const localDates = (dates?: Date[]): Date[] | undefined => dates && dates.map((date: Date) => localDate(date)!);

// import DatePicker, { registerLocale } from 'react-datepicker';

/** See documentation for localDate utility function */
const localDateRange = (tzDateRange?: DateRange): DateRange | undefined => {
  return tzDateRange && { from: localDate(tzDateRange.from), to: localDate(tzDateRange.to) };
};

/** Reverse functionality to localDate / localDateRange utilities above - useful for usage with date
 * components that don't have timezone support like DayPicker (react-day-picker). This utility applies
 * the app timezone on the input date, preserving its date and time values, for example:
 * CST Date Input:  Mon Dec 05 2022 11:26:10 GMT-0600 (CST, local-machine timezone)
 * UTC Date Output: Mon Dec 05 2022 11:26:10 GMT-0000 (UTC, app-settings timezone)
 */
const tzDate = (date?: Date): Date | undefined => {
  const dateFnsFormat = 'yyyy/MM/dd hh:mm:ss';
  const momentFormat = 'YYYY/MM/DD hh:mm:ss';

  return date && moment(format(date, dateFnsFormat), momentFormat).toDate();
};

/** See documentation for tzDate utility function */
const tzDates = (dates?: Date[]): Date[] | undefined => dates && dates.map((date: Date) => tzDate(date)!);

/** See documentation for tzDate utility function */
const tzDateRange = (localDateRange?: DateRange): DateRange | undefined => {
  return localDateRange && { from: tzDate(localDateRange.from), to: tzDate(localDateRange.to) };
};

/**
 * DayPicker: Moovex adapter for react-day-picker's DayPicker, making it time-zone agnostic:
 * "props.selected" value is adapted automatically from the app system-settings time zone to local-machine time-zone.
 * "props.onSelect" events are propagated with adapted values from local-machine time-zone to app system-settings time-zone.
 * @returns {React.ReactElement} Time zone agnostic DayPicker component.
 */
export const DayPicker: React.FC<DayPickerProps> = (props: DayPickerProps) => {
  // const [locale, setLocale] = useState(en); // Default to English

  const [localeState] = useLocaleState();

  if (isDayPickerSingle(props)) {
    return (
      <ReactDayPicker
        {...props}
        selected={localDate(props.selected)}
        onSelect={(day: Date | undefined, selectedDay: Date, activeModifiers: ActiveModifiers, e: React.MouseEvent) =>
          props.onSelect?.(tzDate(day), tzDate(selectedDay)!, activeModifiers, e)
        }
        dir={getCurrentDirection()}
        locale={localeState}
        weekStartsOn={getCurrentDirection() === LanguageDirection.RTL ? 0 : 1}
      />
    );
  }

  if (isDayPickerRange(props)) {
    return (
      <ReactDayPicker
        {...props}
        selected={localDateRange(props.selected)}
        onSelect={(range: DateRange | undefined, selectedDay: Date, activeModifiers: ActiveModifiers, e: React.MouseEvent) =>
          props.onSelect?.(tzDateRange(range), tzDate(selectedDay)!, activeModifiers, e)
        }
        dir={getCurrentDirection()}
        locale={localeState}
        weekStartsOn={getCurrentDirection() === LanguageDirection.RTL ? 0 : 1}
      />
    );
  }

  if (isDayPickerMultiple(props)) {
    return (
      <ReactDayPicker
        {...props}
        selected={localDates(props.selected)}
        onSelect={(days: Date[] | undefined, selectedDay: Date, activeModifiers: ActiveModifiers, e: React.MouseEvent) =>
          props.onSelect?.(tzDates(days), tzDate(selectedDay)!, activeModifiers, e)
        }
        dir={getCurrentDirection()}
        locale={localeState}
        weekStartsOn={getCurrentDirection() === LanguageDirection.RTL ? 0 : 1}
      />
    );
  }

  throw new Error(`Please update DayPicker to support timezone for current mode (${props.mode || 'default'})`);
};
